import {
  useAttachTravelEsimQrCode,
  useSendTravelEsimQrCode,
} from '@/hooks/useTravelEsimOrderActions';
import type { OrderItem } from '@octave/contracts';
import { Loader2, QrCode, Send, Upload } from 'lucide-react';
import { useRef } from 'react';
import { cn } from '@/lib/utils';

/** The catalog's emerald pill for the one action that moves the order forward. */
const primaryClass =
  'rounded-full bg-emerald-600 px-4 py-2 text-white shadow-sm hover:bg-emerald-700 hover:shadow-md';
/** The line detail's quiet grey button for secondary actions. */
const secondaryClass =
  'rounded-xl bg-slate-50 px-4 py-2 text-slate-600 hover:bg-slate-100';

interface TravelEsimQrCodeUploaderProps {
  orderId: string;
  item: OrderItem;
}

/**
 * Admin controls of a travel eSIM order line: upload the QR image generated
 * on the provider's tool (check it with "Voir le QR code") and send it to
 * the beneficiary. With the eSIM number recorded (in any order), the line
 * is served; the server closes the order once every line is.
 */
export function TravelEsimQrCodeUploader({
  orderId,
  item,
}: TravelEsimQrCodeUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const attach = useAttachTravelEsimQrCode(orderId);
  const send = useSendTravelEsimQrCode(orderId);
  if (item.metadata.type !== 'TRAVEL_ESIM') return null;
  const attached = !!item.metadata.qrCodePath;
  const sent = !!item.metadata.qrCodeSentAt;
  const busy = attach.isPending || send.isPending;

  return (
    <div className="flex flex-col items-end gap-2">
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) attach.mutate({ itemId: item.id, file });
        }}
      />
      <button
        type="button"
        disabled={busy}
        onClick={() => inputRef.current?.click()}
        className={cn(
          'inline-flex items-center gap-1.5 text-sm font-medium transition-all disabled:opacity-50 cursor-pointer',
          attached
            ? secondaryClass
            : primaryClass,
        )}
      >
        {attach.isPending ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : attached ? (
          <Upload className="h-3.5 w-3.5" />
        ) : (
          <QrCode className="h-3.5 w-3.5" />
        )}
        {attached ? 'Remplacer le QR code' : 'Téléverser le QR code'}
      </button>
      {attached && (
        <button
          type="button"
          disabled={busy}
          onClick={() => send.mutate(item.id)}
          className={cn(
            'inline-flex items-center gap-1.5 text-sm font-medium transition-all disabled:opacity-50 cursor-pointer',
            sent ? secondaryClass : primaryClass,
          )}
        >
          {send.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Send className="h-3.5 w-3.5" />
          )}
          {sent ? 'Renvoyer au bénéficiaire' : 'Envoyer au bénéficiaire'}
        </button>
      )}

    </div>
  );
}
